import { useState } from "react";
import { Box } from "@mui/material";
import { LineItem } from "../../shared/types";
import { EmailRecipient } from "../../EmailRecipients";
import { PaymentMethod, PaymentType } from "../../PaymentMethods";
import { ScopeOfWork } from "../../../../scope-of-work/types/scope-of-work.interface";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";
import { ClientInvoiceHeader } from "@components/invoice/view/components/ClientView/ClientInvoiceHeader";
import { InvoiceEmailPreview } from "./InvoiceEmailPreview";
import { InvoiceCustomStyle } from "@components/invoice-customization/types/invoiceCustomStyle.types";
import { useCurrentCompanyInfo } from "src/middlewares/hooks/useCurrentCompanyInfo";
import { formatInvoiceAppliesToNote } from "../../shared/utils";

interface ClientPreviewProps {
  clientName?: string;
  clientMail?: string;
  lineItems: LineItem[];
  emailRecipients: EmailRecipient[];
  paymentMethod: PaymentMethod;
  paymentType: PaymentType;
  referenceNumber: string;
  scopeOfWork: ScopeOfWork;
  serviceFeeLabel: string;
  selectedPayments: string[];
  identifierId?: string;
  issuedDate?: string;
  onExit: () => void;
  invoiceDetails: Record<string, any>;
  invoiceCustomStyle: InvoiceCustomStyle;
  previewMode: "invoice" | "email";
}

export const ClientPreview = ({
  invoiceCustomStyle,
  clientName,
  clientMail,
  paymentMethod,
  paymentType,
  referenceNumber,
  scopeOfWork,
  serviceFeeLabel,
  selectedPayments,
  identifierId,
  issuedDate,
  invoiceDetails,
  previewMode,
}: ClientPreviewProps) => {
  const token = sessionStorage.getItem("token");

  const activeCompanyId = useSelector((state: RootState) => state.userState.activeCompanyId);

  const { data: company } = useCurrentCompanyInfo(token, activeCompanyId);

  // Compañía activa desde Redux.
  // Esta era la fuente que funcionaba correctamente para el nombre.
  const activeCompany = (useSelector((state: RootState) => state.userState.company) ?? {}) as any;

  // Nombre de compañía:
  // primero Redux, igual que antes.
  const companyName = activeCompany?.businessName || activeCompany?.dba || company?.businessName || company?.dba || "Flownect";

  // Dirección: se mantiene usando company para conservar
  // el comportamiento que ya te estaba funcionando.
  const companyAddress = [
    company?.address || activeCompany?.address,
    company?.city || activeCompany?.city,
    company?.state || activeCompany?.state,
    company?.zipCode || activeCompany?.zipCode,
    company?.country || activeCompany?.country,
  ]
    .filter(Boolean)
    .join(", ");

  // Email: se mantiene usando company primero.
  const companyEmail = company?.email || activeCompany?.email || activeCompany?.businessEmail || "";

  // Logo
  const companyLogoUrl =
    company?.avatar ||
    activeCompany?.avatar ||
    activeCompany?.logoUrl ||
    activeCompany?.avatarUrl ||
    activeCompany?.imageUrl ||
    activeCompany?.logo ||
    "/logo.png";

  const [currentViewerPermission] = useState<"Can Pay" | "Can View">("Can Pay");

  const canViewPayment = selectedPayments.length > 0 && currentViewerPermission === "Can Pay";

  const scopeAny = scopeOfWork as any;
  const calculations = scopeAny?.data ?? {};

  const formatUsdAmount = (amount: number, negative = false) =>
    `${negative ? "-" : ""}USD ${Math.abs(amount).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const emailExtraLineItems =
    invoiceDetails?.pricingDetail?.extraLineItems?.map((item: any) => ({
      title: item?.title ?? "Additional item",
      description: item?.description,
      amount: formatUsdAmount(Number(item?.amount ?? 0)),
    })) ?? [];

  const emailDiscounts =
    (invoiceDetails?.pricingDetail?.discounts ?? calculations?.discounts ?? [])
      .map((item: any) => ({
        title: String(item?.description ?? item?.title ?? "").trim() || "Discount",
        description: formatInvoiceAppliesToNote(item?.appliesTo),
        amount: formatUsdAmount(Number(item?.amount ?? 0), true),
      }))
      .filter((item: { title: string; amount: string }) => item.title.length > 0) ?? [];

  const emailServiceFees = Array.isArray(invoiceDetails?.pricingDetail?.serviceFees)
    ? invoiceDetails.pricingDetail.serviceFees
        .filter((item: any) => Number(item?.total?.value ?? item?.amount ?? 0) > 0)
        .map((item: any) => ({
          title: String(item?.alias ?? serviceFeeLabel ?? "Service fee").trim() || "Service fee",
          description: formatInvoiceAppliesToNote(item?.appliesTo),
          amount: formatUsdAmount(Number(item?.total?.value ?? item?.amount ?? 0)),
        }))
    : [];

  const serviceLine = calculations?.pricingDetail?.serviceLine ?? {};

  const previewJob = invoiceDetails?.billingJobEvents?.[0]?.job ?? invoiceDetails?.job ?? null;

  const serviceNameOverride = scopeAny?.serviceNameOverride?.trim() || null;

  const jobAlias = previewJob?.alias?.trim() || null;

  const hasCustomServiceName = Boolean(serviceNameOverride || jobAlias);

  const serviceName = serviceNameOverride || jobAlias || scopeAny?.typeOfService?.name?.trim() || scopeAny?.name?.trim() || "";

  const serviceDescription = hasCustomServiceName ? previewJob?.description?.trim() || "" : scopeAny?.typeOfService?.description?.trim() || "";

  const areas = scopeAny?.areasByScopesOfWork?.map((item: any) => item?.area?.name || item?.name).filter(Boolean) ?? [];

  const specialRequests =
    scopeAny?.specialRequests?.map((item: any) => item?.name || item?.specialRequest?.name || item?.description).filter(Boolean) ?? [];

  const additionalObjects =
    scopeAny?.additionalObjects?.map((item: any) => item?.name || item?.additionalObject?.name || item?.object?.name).filter(Boolean) ?? [];

  const invoiceServiceSummary = {
    serviceName,
    serviceDescription,

    typeOfPlace: scopeAny?.typeOfPlace?.name || scopeAny?.placeType?.name || "",

    frequency: scopeAny?.frequency?.name || scopeAny?.frequencyType?.name || scopeAny?.frequency || scopeAny?.frequencyType || "",

    squareFootage:
      scopeAny?.squareFootage?.name ||
      scopeAny?.squareFootage?.value ||
      scopeAny?.squareFootage ||
      scopeAny?.typeOfPlace?.squareFootage ||
      scopeAny?.sqft ||
      "",

    areas,
    specialRequests,
    additionalObjects,

    quantity: Number(serviceLine?.qty ?? 0),

    unitPrice: Number(serviceLine?.unitPrice ?? 0),

    serviceLines: Array.isArray(calculations?.pricingDetail?.serviceLines) ? calculations.pricingDetail.serviceLines : [],

    mainAreasTotal: Number(calculations?.subtotal ?? 0),

    serviceFeeTotal: Number(calculations?.serviceFeeAmount ?? 0),

    specialRequestsTotal: Number(calculations?.specialRequestsTotal ?? scopeAny?.specialRequestsCost?.total?.value ?? 0),

    additionalObjectsTotal: Number(calculations?.additionalObjectsTotal ?? scopeAny?.additionalObjectsCost?.total?.value ?? 0),

    otherAreasTotal: Number(calculations?.otherAreasTotal ?? scopeAny?.otherAreasCost?.total?.value ?? 0),

    total: Number(calculations?.total ?? 0),

    currency: "USD",
  };

  const previewPaymentMethods = selectedPayments.map((provider) => ({
    provider,
    bankTransferInfo:
      provider === "bank_transfer"
        ? {
            accountHolderName: paymentMethod?.bankTransferInfo?.accountHolderName,
            routingNumber: paymentMethod?.bankTransferInfo?.routingNumber,
            accountNumber: paymentMethod?.bankTransferInfo?.accountNumber,
          }
        : undefined,
  }));

  const effectiveFeeLabel = serviceFeeLabel || invoiceCustomStyle?.labels?.increasePercentageByFrequency || "Service fee";

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
      }}
    >
      {previewMode === "invoice" ? (
        <ClientInvoiceHeader
          isPdfMode={true}
          paymentStatus={false}
          billingContactName={clientName}
          billingEmail={clientMail}
          calculations={(scopeOfWork as any)?.data}
          serviceFeeLabel={effectiveFeeLabel}
          paymentMethods={previewPaymentMethods}
          paymentType={paymentType}
          referenceNumber={referenceNumber}
          canViewPayment={canViewPayment}
          total={(scopeOfWork as any)?.data?.total ?? 0}
          selectedPayments={selectedPayments}
          identifierId={identifierId}
          issuedDate={issuedDate}
          invoiceDetails={invoiceDetails}
          scopeOfWork={scopeOfWork}
          notes={invoiceDetails?.notes ?? undefined}
          invoiceCustomStyle={invoiceCustomStyle}
          companyGmail={companyEmail}
          companyName={companyName}
          companyAddress={companyAddress}
          companyTimeZone={company?.timeZone || activeCompany?.timeZone}
        />
      ) : (
        <InvoiceEmailPreview
          invoiceCustomStyle={invoiceCustomStyle}
          logoUrl={companyLogoUrl}
          companyName={companyName}
          companyEmail={companyEmail}
          invoiceNumber="Generated after creation"
          amount={`USD ${Number(calculations?.total ?? 0).toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`}
          dueDate={issuedDate}
          canPay={canViewPayment}
          dashboardUrl="#"
          serviceFeeLabel={effectiveFeeLabel}
          notes={invoiceDetails?.notes ?? ""}
          paymentMethods={previewPaymentMethods}
          extraLineItems={emailExtraLineItems}
          discounts={emailDiscounts}
          serviceFees={emailServiceFees}
          invoiceServiceSummary={invoiceServiceSummary}
          companyAddress={companyAddress}
        />
      )}
    </Box>
  );
};
